// docIdInput/docIdFieldCheck.js — stops the documents.php filter form from searching with an invalid document id
// (chef list Nr. 6a): before, text in #filterDocID gave the whole list; the server now finds nothing for it and says
// why (documents_ui_helpers.php adminFilterDocIdProblemText) — this check says it before anything is sent.
//
// A submit listener in the capture phase on the document sees the submit before documents.js's jQuery handler on the
// form (no change in documents.js) and stops it when tab 1 applies (filter_mode empty), an id mode is chosen and the
// field holds a part that is neither a number nor a field rule. The field is then marked and the message line shown —
// the same markup as the server's; typing in the field clears it. "[id]" passes: whether that document is in a group
// only the server knows (its 'nogroup' message). Paging links do not submit — the server covers them.
//
// createDocIdFieldCheck({ root, form, field, modeField }) → { destroy }

import { createDisposers } from '../core/disposers.js';
import { str } from '../i18n/str.js';
import { parseDocIdInput } from './parseDocIdInput.js';

export function createDocIdFieldCheck({
    root = document, form = '#searchForm', field = '#filterDocID', modeField = 'filter_document_id',
} = {}) {
    const disposers = createDisposers();
    const doc = root.ownerDocument || root;

    const problemLine = (input) => doc.getElementById(`${input.id}Problem`);

    const clear = (input) => {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
        problemLine(input)?.remove();
    };

    const show = (input, part) => {
        const id = `${input.id}Problem`;
        let line = problemLine(input);
        if (!line) {
            line = doc.createElement('div');
            line.className = 'filter-problem';
            line.id = id;
            line.setAttribute('role', 'alert');
            (input.closest('.filter-row') ?? input).after(line);
        }
        line.textContent = str('FILTER_DOCID_INVALID').replace('{part}', part);
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', id);
        input.focus();
    };

    const onSubmit = (ev) => {
        const target = ev.target;
        if (!(target instanceof doc.defaultView.HTMLFormElement) || !target.matches(form)) {
            return;
        }
        const input = target.querySelector(field);
        const tabOne = (target.elements.namedItem('filter_mode')?.value ?? '') === '';
        const mode = target.elements.namedItem(modeField)?.value ?? '';
        if (!input || !tabOne || mode === '') {
            return;
        }
        const invalid = parseDocIdInput(input.value).problems.find((p) => p.reason === 'invalid');
        if (invalid) {
            ev.preventDefault();
            ev.stopPropagation();   // capture phase: the form's own (jQuery) handler does not run
            show(input, invalid.part);
        }
    };
    doc.addEventListener('submit', onSubmit, true);
    disposers.add(() => doc.removeEventListener('submit', onSubmit, true));

    const onInput = (ev) => {
        const input = ev.target;
        if (input instanceof doc.defaultView.HTMLInputElement && input.matches(field) && input.hasAttribute('aria-invalid')) {
            clear(input);
        }
    };
    doc.addEventListener('input', onInput);
    disposers.add(() => doc.removeEventListener('input', onInput));

    return { destroy: () => disposers.dispose() };
}
